import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building, Percent, Calendar, Settings, ChevronRight, PartyPopper, Timer, Calculator } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { getCreationDuration, getAcreEndDate, getVLRate } from "../utils/helpers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

// Onboarding Page
export const OnboardingPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [profile, setProfile] = useState({
    date_creation_entreprise: "", type_activite: "", activite: "", double_activite: false,
    type_activite_secondaire: "", activite_secondaire: "", versement_liberatoire: "", acre: "", declaration_frequence: "mensuel"
  });
  const api = useApi();
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const handleSave = async () => {
    setLoading(true);
    try {
      const profileData = { ...profile, versement_liberatoire: profile.versement_liberatoire === "oui", acre: profile.acre === "oui" };
      await api.put("/profile", profileData);
      await api.post("/profile/complete-onboarding");
      
      // Vérifier si un code parrainage est en attente d'application
      const pendingReferralCode = sessionStorage.getItem("pending_referral_code");
      const pendingReferralPrenom = sessionStorage.getItem("pending_referral_prenom");
      
      if (pendingReferralCode) {
        try {
          await api.post("/referral/apply", { referral_code: pendingReferralCode });
          sessionStorage.removeItem("pending_referral_code");
          sessionStorage.removeItem("pending_referral_prenom");
          toast.success(`🎁 Code parrainage appliqué ! 1 mois gratuit offert par ${pendingReferralPrenom || "ton parrain"}`);
          
          // Récupérer le profil mis à jour
          const updatedProfile = await api.get("/profile");
          updateUser({ ...updatedProfile.data, onboarding_complete: true });
          setHasSubscription(true);
          setCompleted(true);
          
          // Aller directement au dashboard
          setTimeout(() => {
            navigate("/");
          }, 3000);
          setLoading(false);
          return;
        } catch (refErr) {
          console.error("Erreur application parrainage:", refErr);
          sessionStorage.removeItem("pending_referral_code");
          sessionStorage.removeItem("pending_referral_prenom");
        }
      }
      
      // Récupérer le profil mis à jour pour vérifier le statut d'abonnement
      const updatedProfile = await api.get("/profile");
      const hasActiveSubscription = updatedProfile.data.subscription_status === "active";
      
      setHasSubscription(hasActiveSubscription);
      updateUser({ ...updatedProfile.data, onboarding_complete: true });
      setCompleted(true);
      
      // Rediriger vers le dashboard si abonnement actif, sinon vers l'abonnement standard
      setTimeout(() => {
        if (hasActiveSubscription) {
          navigate("/");
        } else {
          navigate("/subscription");
        }
      }, 3000);
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    }
    setLoading(false);
  };

  const canProceedStep1 = profile.date_creation_entreprise && profile.type_activite && profile.activite && (!profile.double_activite || (profile.type_activite_secondaire && profile.activite_secondaire));
  const canProceedStep2 = profile.versement_liberatoire && profile.acre;

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center p-4">
        <div className="text-center animate-slide-up">
          <div className="text-6xl mb-6">✨</div>
          <h1 className="text-3xl font-bold mb-4">Biz360 <span className="text-red-500">❤️‍🔥</span> est entièrement configuré pour toi !</h1>
          <p className="text-xl text-muted-foreground">
            {hasSubscription ? "Redirection vers ton tableau de bord..." : "Plus qu'une étape..."}
          </p>
          <div className="mt-8"><div className="animate-spin w-6 h-6 border-4 border-primary border-t-transparent rounded-full mx-auto" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Pour utiliser Biz360 <span className="text-red-500">❤️‍🔥</span>, complète ton profil</h1>
          <p className="text-muted-foreground">Ces informations vont permettre de calculer automatiquement tes cotisations, suivre tes seuils et adapter ton tableau de bord.</p>
        </div>
        
        {/* Barre de progression améliorée */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: '#897FCB' }}>Étape {step}/3</span>
            <span className="text-sm text-muted-foreground">
              {step === 1 && "📋 Tes informations de base"}
              {step === 2 && "💰 Tes options fiscales"}
              {step === 3 && "📅 Ta fréquence de déclaration"}
            </span>
          </div>
          <div className="flex gap-2">
            {[1, 2, 3].map((s) => (
              <div 
                key={s} 
                className={`h-3 flex-1 rounded-full transition-all duration-300 ${s <= step ? '' : 'bg-muted'}`}
                style={{ backgroundColor: s <= step ? '#897FCB' : undefined }}
              />
            ))}
          </div>
          <p className="text-center text-sm mt-3" style={{ color: '#059669' }}>
            {step === 1 && "✨ C'est parti ! Quelques infos et ton dashboard sera prêt."}
            {step === 2 && "💪 Encore une étape après celle-ci !"}
            {step === 3 && "🎯 Dernière étape ! Tu y es presque !"}
          </p>
        </div>
        
        <div className="bg-card rounded-xl border p-8 shadow-lg animate-slide-up">
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold flex items-center gap-2"><Building size={24} /> Mes Infos</h2>
              <div>
                <label className="block text-sm font-medium mb-2">Date de création de l'entreprise</label>
                <input type="date" data-testid="onboarding-date-creation" value={profile.date_creation_entreprise}
                  onChange={(e) => setProfile({ ...profile, date_creation_entreprise: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-input bg-background" />
                {profile.date_creation_entreprise && (
                  <p className="mt-2 text-sm text-emerald-600 font-medium flex items-center gap-1"><PartyPopper size={16} /> {getCreationDuration(profile.date_creation_entreprise)}</p>
                )}
              </div>
              <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                <h3 className="font-medium">Infos de base</h3>
                <div>
                  <label className="block text-sm font-medium mb-2">Type d'activité</label>
                  <Select 
                    value={profile.type_activite} 
                    onValueChange={(value) => setProfile({ ...profile, type_activite: value })}
                  >
                    <SelectTrigger className="w-full h-11" data-testid="onboarding-type-activite">
                      <SelectValue placeholder="Sélectionner le type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vente de marchandises">Vente de marchandises</SelectItem>
                      <SelectItem value="prestation de services">Prestation de services</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Activité</label>
                  <Select 
                    value={profile.activite} 
                    onValueChange={(value) => setProfile({ ...profile, activite: value })}
                  >
                    <SelectTrigger className="w-full h-11" data-testid="onboarding-activite">
                      <SelectValue placeholder="Sélectionner l'activité..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="artisanale">Artisanale</SelectItem>
                      <SelectItem value="commerciale">Commerciale</SelectItem>
                      <SelectItem value="libérale">Libérale</SelectItem>
                      <SelectItem value="libérale réglementée">Libérale Réglementée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg border hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => setProfile({ ...profile, double_activite: !profile.double_activite })}>
                <input type="checkbox" id="double_activite" data-testid="onboarding-double-activite" checked={profile.double_activite}
                  onChange={(e) => setProfile({ ...profile, double_activite: e.target.checked })} className="w-5 h-5 rounded border-input" />
                <label htmlFor="double_activite" className="text-sm font-medium cursor-pointer">J'ai une double activité</label>
              </div>
              {profile.double_activite && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg space-y-4 border-l-4 border-blue-500 animate-slide-up">
                  <h3 className="font-medium text-blue-700 dark:text-blue-400">Activité secondaire</h3>
                  <div>
                    <label className="block text-sm font-medium mb-2">Type d'activité</label>
                    <Select 
                      value={profile.type_activite_secondaire} 
                      onValueChange={(value) => setProfile({ ...profile, type_activite_secondaire: value })}
                    >
                      <SelectTrigger className="w-full h-11" data-testid="onboarding-type-activite-secondaire">
                        <SelectValue placeholder="Sélectionner le type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vente de marchandises">Vente de marchandises</SelectItem>
                        <SelectItem value="prestation de services">Prestation de services</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Activité</label>
                    <Select 
                      value={profile.activite_secondaire} 
                      onValueChange={(value) => setProfile({ ...profile, activite_secondaire: value })}
                    >
                      <SelectTrigger className="w-full h-11" data-testid="onboarding-activite-secondaire">
                        <SelectValue placeholder="Sélectionner l'activité..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="artisanale">Artisanale</SelectItem>
                        <SelectItem value="commerciale">Commerciale</SelectItem>
                        <SelectItem value="libérale">Libérale</SelectItem>
                        <SelectItem value="libérale réglementée">Libérale Réglementée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold flex items-center gap-2"><Percent size={24} /> Options fiscales</h2>
              <div className="space-y-4">
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <label className="font-medium">Versement libératoire</label>
                    <Select 
                      value={profile.versement_liberatoire} 
                      onValueChange={(value) => setProfile({ ...profile, versement_liberatoire: value })}
                    >
                      <SelectTrigger className="w-32 h-10" data-testid="onboarding-versement-liberatoire">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oui">Oui</SelectItem>
                        <SelectItem value="non">Non</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {profile.versement_liberatoire === "oui" && profile.type_activite && profile.activite && (
                    <p className="text-sm text-blue-600 bg-blue-50 dark:bg-blue-950/30 p-2 rounded">
                      <Calculator size={14} className="inline mr-1" />Taux applicable : <strong>{getVLRate(profile.type_activite, profile.activite).display}</strong> de mon CA
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground mt-2">Payer l'impôt sur le revenu en même temps que les cotisations sociales</p>
                </div>
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <label className="font-medium">ACRE</label>
                    <Select 
                      value={profile.acre} 
                      onValueChange={(value) => setProfile({ ...profile, acre: value })}
                    >
                      <SelectTrigger className="w-32 h-10" data-testid="onboarding-acre">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oui">Oui</SelectItem>
                        <SelectItem value="non">Non</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {profile.acre === "oui" && profile.date_creation_entreprise && (
                    <p className="text-sm text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 p-2 rounded">
                      <Timer size={14} className="inline mr-1" />ACRE actif jusqu'au : <strong>{getAcreEndDate(profile.date_creation_entreprise)}</strong>
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground mt-2">Aide aux Créateurs et Repreneurs d'Entreprise (-50% de cotisations la 1ère année)</p>
                </div>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold flex items-center gap-2"><Calendar size={24} /> Option Urssaf</h2>
              <div>
                <label className="block text-sm font-medium mb-3">Déclaration mensuelle ou trimestrielle</label>
                <div className="grid grid-cols-2 gap-4">
                  <button type="button" data-testid="onboarding-frequence-mensuel" onClick={() => setProfile({ ...profile, declaration_frequence: "mensuel" })}
                    className={`p-4 rounded-lg border-2 transition-all ${profile.declaration_frequence === "mensuel" ? "border-primary bg-primary/5" : "border-input hover:border-primary/50"}`}>
                    <div className="font-medium">Mensuel</div><div className="text-sm text-muted-foreground mt-1">Chaque mois</div>
                  </button>
                  <button type="button" data-testid="onboarding-frequence-trimestriel" onClick={() => setProfile({ ...profile, declaration_frequence: "trimestriel" })}
                    className={`p-4 rounded-lg border-2 transition-all ${profile.declaration_frequence === "trimestriel" ? "border-primary bg-primary/5" : "border-input hover:border-primary/50"}`}>
                    <div className="font-medium">Trimestriel</div><div className="text-sm text-muted-foreground mt-1">Tous les 3 mois</div>
                  </button>
                </div>
              </div>
              <div className="p-4 rounded-lg border-2" style={{ backgroundColor: '#897FCB15', borderColor: '#897FCB' }}>
                <p className="text-sm flex items-center gap-2" style={{ color: '#210875' }}><Settings size={16} className="text-[#897FCB]" />Je peux modifier mes paramètres en 2 clics via le menu <strong style={{ color: '#897FCB' }}>Mes Paramètres</strong></p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 border">
                <h3 className="font-medium mb-3">Récapitulatif</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between"><span className="text-muted-foreground">Activité principale :</span><span className="font-medium">{profile.type_activite} ({profile.activite})</span></li>
                  {profile.double_activite && (<li className="flex justify-between"><span className="text-muted-foreground">Activité secondaire :</span><span className="font-medium">{profile.type_activite_secondaire} ({profile.activite_secondaire})</span></li>)}
                  <li className="flex justify-between"><span className="text-muted-foreground">Versement libératoire :</span><span className="font-medium">{profile.versement_liberatoire === "oui" ? "Oui" : "Non"}</span></li>
                  <li className="flex justify-between"><span className="text-muted-foreground">ACRE :</span><span className="font-medium">{profile.acre === "oui" ? "Oui" : "Non"}</span></li>
                  <li className="flex justify-between"><span className="text-muted-foreground">Déclarations :</span><span className="font-medium capitalize">{profile.declaration_frequence}</span></li>
                </ul>
              </div>
            </div>
          )}
          <div className="flex justify-between mt-8">
            {step > 1 ? (<button onClick={() => setStep(step - 1)} className="px-6 h-11 rounded-lg border border-input hover:bg-muted transition-colors">Retour</button>) : <div />}
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} data-testid="onboarding-next" disabled={(step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2)}
                className="px-6 h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                Suivant <ChevronRight size={18} />
              </button>
            ) : (
              <button onClick={handleSave} data-testid="onboarding-finish" disabled={loading}
                className="px-6 h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
                {loading ? "Enregistrement..." : "Terminer ✨"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
